import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  ArrowLeft, 
  Map, 
  Home, 
  CreditCard, 
  FileText, 
  BookOpen, 
  HelpCircle,
  Shield,
  Scale,
  LayoutDashboard,
  Plus,
  Library,
  Settings,
  Sparkles,
  BarChart3,
  TrendingUp
} from "lucide-react";

export default function Sitemap() {
  const navigate = useNavigate();

  const siteStructure = [
    {
      category: "Public Pages",
      icon: <Home className="w-5 h-5 text-blue-600" />,
      pages: [
        { name: "Home", path: "/", description: "Landing page with app overview and login" },
        { name: "Pricing", path: "/pricing", description: "Subscription plans and pricing information" },
        { name: "Try It Now", path: "/try", description: "Free demo to test requisition generation" },
        { name: "FAQ", path: "/faq", description: "Frequently asked questions" },
        { name: "User Guide", path: "/guide", description: "Tips to maximize SmartReqs value" },
      ]
    },
    {
      category: "User Dashboard",
      icon: <LayoutDashboard className="w-5 h-5 text-emerald-600" />,
      pages: [
        { name: "Dashboard", path: "/dashboard", description: "View and manage your requisitions", requiresAuth: true },
        { name: "Create Requisition", path: "/builder", description: "Generate new job requisitions with AI", requiresAuth: true },
        { name: "Requisition Library", path: "/library", description: "Browse and manage saved requisitions", requiresAuth: true },
        { name: "Settings", path: "/settings", description: "Account and subscription management", requiresAuth: true },
      ]
    },
    {
      category: "Requisition Features",
      icon: <FileText className="w-5 h-5 text-purple-600" />,
      pages: [
        { name: "Requisition Detail", path: "/requisition/:id", description: "View complete requisition with all sections", requiresAuth: true },
        { name: "Market Analysis", path: "/requisition/:id/market", description: "Salary data and market insights", requiresAuth: true },
        { name: "Competitive Intelligence", path: "/requisition/:id/intelligence", description: "Skills, requirements, and competitive data", requiresAuth: true },
      ]
    },
    {
      category: "Legal & Policies",
      icon: <Scale className="w-5 h-5 text-slate-600" />,
      pages: [
        { name: "Terms of Service", path: "/terms-of-service", description: "Service terms and conditions" },
        { name: "Privacy Policy", path: "/privacy-policy", description: "How we handle your data" },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2" data-testid="sitemap-back-btn">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-slate-900">Sitemap</h1>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-900 text-white py-10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-white/20 rounded-full">
              <Map className="w-8 h-8" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-2">Site Map</h2>
          <p className="text-slate-300">
            Complete overview of all pages and features in SmartReqs
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {siteStructure.map((section, sectionIndex) => (
            <Card key={sectionIndex} className="overflow-hidden">
              <CardHeader className="bg-slate-50 border-b border-slate-100">
                <CardTitle className="flex items-center gap-3 text-lg">
                  {section.icon}
                  {section.category}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y divide-slate-100">
                  {section.pages.map((page, pageIndex) => (
                    <li key={pageIndex}>
                      <button
                        onClick={() => {
                          if (!page.path.includes(':id')) {
                            navigate(page.path);
                          }
                        }}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${
                          page.path.includes(':id') ? 'cursor-default' : 'cursor-pointer'
                        }`}
                        disabled={page.path.includes(':id')}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-medium text-slate-900 flex items-center gap-2">
                              {page.name}
                              {page.requiresAuth && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                  Login Required
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-slate-500 mt-0.5">{page.description}</p>
                          </div>
                          {!page.path.includes(':id') && (
                            <span className="text-slate-400 text-sm shrink-0">{page.path}</span>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Features Summary */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Key Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">AI Requisition Generation</p>
                  <p className="text-xs text-slate-500">GPT-powered job descriptions</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <BarChart3 className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">Market Analysis</p>
                  <p className="text-xs text-slate-500">Real-time salary data</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">Competitive Intelligence</p>
                  <p className="text-xs text-slate-500">Skills & requirements insights</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Library className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">Requisition Library</p>
                  <p className="text-xs text-slate-500">Save & reuse templates</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-rose-100 rounded-lg">
                  <FileText className="w-4 h-4 text-rose-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">Export Options</p>
                  <p className="text-xs text-slate-500">PDF, Word, Text formats</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Shield className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">Secure & Private</p>
                  <p className="text-xs text-slate-500">Your data stays yours</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Questions? Contact us at{" "}
            <a href="mailto:admin@smartreqs.ai" className="text-blue-600 hover:underline">
              admin@smartreqs.ai
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
